import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
  </svg>
);

const PrayerRequestModal = ({ isOpen, onClose }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  if (!isOpen) return null;

  // Tooltip content for each request type
  const tooltipContent = {
    homa: "The homa fire ritual is conducted on the 28th of each month at Shinnyo-en's main temple in Japan. When requesting homa prayers, you can express gratitude towards buddhas, make personal requests and ask for spiritual purification for specific individuals or situations by using the \"Request for Homa\" forms. The homa ritual is a tool for focusing our minds on the altruistic path of change and is an important form of support on our path of spiritual growth and awakening.",
    merit: "Spiritual consolation or merit transfer is a ritual practice where merit is offered to the deceased. This practice helps us acknowledge those who came before us with a sense of gratitude and reminds us to make the most of the here and now; connecting the past, present and inviting hope for the future. Please write down the names of those whom you wish to dedicate your consolatory prayers on this form and make a small monetary offering as a gesture of your sincere intent.",
    seasonal: "There are other homa and/or spiritual consolatory rituals conducted annually at a specific time of the year. Special request forms are used for these rituals."
  };

  const styles = {
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1001,
      padding: isMobile ? "10px" : "20px",
    },
    modalContent: {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: isMobile ? "20px" : isTablet ? "25px" : "30px",
      maxWidth: isMobile ? "100%" : isTablet ? "600px" : "800px",
      width: isMobile ? "95%" : "90%",
      position: "relative",
      boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
      maxHeight: isMobile ? "90vh" : "80vh",
      overflowY: "auto",
    },
    modalHeader: {
      fontSize: isMobile ? "1.2rem" : isTablet ? "1.3rem" : "1.5rem",
      fontWeight: "600",
      marginBottom: isMobile ? "20px" : "30px",
      textAlign: "center",
      color: "#374151",
      lineHeight: "1.3",
    },
    modalGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
      gap: isMobile ? "15px" : "20px",
      marginBottom: isMobile ? "20px" : "30px",
    },
    modalCard: {
      border: "2px solid #e5e7eb",
      borderRadius: "8px",
      padding: isMobile ? "15px" : "20px",
      textAlign: "center",
      backgroundColor: "#f9fafb",
      position: "relative",
      minHeight: isMobile ? "120px" : "140px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    modalCardDisabled: {
      opacity: 0.55,
      filter: "grayscale(60%)",
      cursor: "not-allowed",
    },
    disabledOverlay: {
      position: "absolute",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: isMobile ? "0.75rem" : "0.85rem",
      fontWeight: 600,
      letterSpacing: "0.5px",
      padding: "4px",
      textAlign: "center",
      borderRadius: "6px"
    },
    modalCardTitle: {
      fontSize: isMobile ? "1rem" : "1.1rem",
      fontWeight: "600",
      marginBottom: "15px",
      color: "#374151",
      lineHeight: "1.3",
      paddingRight: "25px",
    },
    modalRequestButton: {
      backgroundColor: "#6b7280",
      color: "white",
      border: "none",
      padding: isMobile ? "8px 16px" : "10px 20px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: isMobile ? "0.8rem" : "0.9rem",
      fontWeight: "600",
      transition: "background-color 0.2s",
      marginTop: "auto",
    },
    modalHelpIcon: {
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: "#fff",
      color: "#000",
      borderRadius: "50%",
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: isMobile ? "12px" : "14px",
      transition: "background-color 0.2s",
    },
    tooltip: {
      position: "absolute",
      top: "35px",
      left: "0",
      backgroundColor: "#ffffff",
      color: "#000000",
      padding: "12px",
      borderRadius: "8px",
      fontSize: "0.85rem",
      lineHeight: "1.4",
      maxWidth: "320px",
      width: "max-content",
      zIndex: 25000,
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      border: "1px solid #e5e7eb",
      opacity: 0,
      transform: "translateY(-5px)",
      transition: "opacity 0.2s, transform 0.2s",
      pointerEvents: "none",
    },
    tooltipTwo: {
      position: "absolute",
      top: "35px",
      right: "0",
      backgroundColor: "#ffffff",
      color: "#000000",
      padding: "12px",
      borderRadius: "8px",
      fontSize: "0.85rem",
      lineHeight: "1.4",
      maxWidth: "320px",
      width: "max-content",
      zIndex: 25000,
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      border: "1px solid #e5e7eb",
      opacity: 0,
      transform: "translateY(-5px)",
      transition: "opacity 0.2s, transform 0.2s",
      pointerEvents: "none",
    },
    tooltipVisible: {
      opacity: 1,
      transform: "translateY(0)",
    },
    tooltipArrow: {
      position: "absolute",
      top: "-6px",
      right: "65px",
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderBottom: "6px solid #ffffff",
    },
    tooltipArrowBorder: {
      position: "absolute",
      top: "-7px",
      right: "65px",
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderBottom: "6px solid #e5e7eb",
    },
    modalCloseButton: {
      backgroundColor: "#6b7280",
      color: "white",
      border: "none",
      padding: isMobile ? "8px 16px" : "10px 20px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: isMobile ? "0.8rem" : "0.9rem",
      float: "right",
      transition: "background-color 0.2s",
    },
    closeIcon: {
      position: "absolute",
      top: "15px",
      right: "15px",
      background: "none",
      border: "none",
      fontSize: isMobile ? "1.2rem" : "1.5rem",
      cursor: "pointer",
      color: "#6b7280",
      padding: "5px",
    },
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button
          style={styles.closeIcon}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f3f4f6";
            e.currentTarget.style.borderRadius = "4px";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <CloseIcon />
        </button>
        <h2 style={styles.modalHeader}>Select the Type of Prayer Request</h2>
        <div style={styles.modalGrid}>
          <div style={styles.modalCard}>
            <div 
              style={styles.modalHelpIcon}
              onMouseEnter={() => setHoveredCard('homa')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <InfoIcon />
            </div>
            {hoveredCard === 'homa' && (
              <div style={{...styles.tooltip, ...styles.tooltipVisible}}>
                <div style={styles.tooltipArrowBorder}></div>
                <div style={styles.tooltipArrow}></div>
                {tooltipContent.homa}
              </div>
            )}
            <h3 style={styles.modalCardTitle}>Homa Requests</h3>
            <button
              style={styles.modalRequestButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#6b7280";
              }}
              onClick={() => {
                onClose();
                navigate("/homa-requests");
              }}
            >
              REQUEST
            </button>
          </div>
          <div style={styles.modalCard}>
            <div 
              style={styles.modalHelpIcon}
              onMouseEnter={() => setHoveredCard('merit')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <InfoIcon />
            </div>
            {hoveredCard === 'merit' && (
              <div style={{...styles.tooltip, ...styles.tooltipVisible}}>
                <div style={styles.tooltipArrowBorder}></div>
                <div style={styles.tooltipArrow}></div>
                {tooltipContent.merit}
              </div>
            )}
            <h3 style={styles.modalCardTitle}>Merit Transfer Requests</h3>
            <button
              style={styles.modalRequestButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#6b7280";
              }}
              onClick={() => {
                onClose();
                navigate("/merit-requests");
              }}
            >
              REQUEST
            </button>
          </div>
          <div style={styles.modalCard}>
            <div 
              style={styles.modalHelpIcon}
              onMouseEnter={() => setHoveredCard('seasonal')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <InfoIcon />
            </div>
            {hoveredCard === 'seasonal' && (
              <div style={{...styles.tooltipTwo, ...styles.tooltipVisible}}>
                <div style={styles.tooltipArrowBorder}></div>
                <div style={styles.tooltipArrow}></div>
                {tooltipContent.seasonal}
              </div>
            )}
            <h3 style={styles.modalCardTitle}>Seasonal Requests</h3>
            <button
              style={styles.modalRequestButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#6b7280";
              }}
              onClick={() => {
                onClose();
                navigate("/seasonal-requests");
              }}
            >
              REQUEST
            </button>
          </div>
        </div>
        <button
          style={styles.modalCloseButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#4b5563";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#6b7280";
          }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default PrayerRequestModal;
